<template>
  <div>
    <div class="d-flex justify-center align-center">
      <datePicker v-model="f.desde" format="YYYY-MM-DD" label="Desde (Obligatorio)" maxToday clearable />
      <datePicker v-model="f.hasta" format="YYYY-MM-DD" label="Hasta (Obligatorio)" maxToday clearable class="mx-3" />

      <v-autocomplete v-model="f.areasSel" label="Área(s)" :items="areas" :loading="loadingAreas"
        :disabled="loadingAreas" multiple chips small-chips clearable hide-selected item-value="idArea"
        item-text="nombre" class="mr-3" />

      <v-autocomplete v-model="f.maquinasSel" label="Máquina(s)" :items="maquinas" :loading="loadingMaquinas"
        :disabled="loadingMaquinas" multiple chips small-chips clearable hide-selected item-value="idMaquinaIns"
        :item-text="nombreMaquina" class="mx-3" />

      <v-btn :loading="loading" color="primary" :disabled="!f.desde || !f.hasta" @click="buscar">
        <v-icon left>mdi-magnify</v-icon>Buscar
      </v-btn>
    </div>

    <v-alert v-if="error" type="error" dense class="mt-4">{{ error }}</v-alert>

    <v-row class="mt-4" dense>
      <v-col cols="12" md="3">
        <v-card class="pa-4 rounded-lg" outlined height="100">
          <div class="caption grey--text mb-1">Paros totales</div>
          <div v-if="loading" class="d-flex align-center justify-center" style="height:44px">
            <v-progress-circular indeterminate color="primary" size="24" />
          </div>
          <div v-else class="text-h5 font-weight-bold">{{ totalParosCnt }}</div>
        </v-card>
      </v-col>
      <v-col cols="12" md="3">
        <v-card class="pa-4 rounded-lg" outlined height="100">
          <div class="caption grey--text mb-1">Programados</div>
          <div v-if="loading" class="d-flex align-center justify-center" style="height:44px">
            <v-progress-circular indeterminate color="primary" size="24" />
          </div>
          <div v-else class="text-h5 font-weight-bold">{{ programadosCnt }}</div>
        </v-card>
      </v-col>
      <v-col cols="12" md="3">
        <v-card class="pa-4 rounded-lg" outlined height="100">
          <div class="caption grey--text mb-1">Porcentaje de tiempo excedido en paros planeados</div>
          <div v-if="loading" class="d-flex align-center justify-center" style="height:44px">
            <v-progress-circular indeterminate color="primary" size="24" />
          </div>
          <div v-else class="text-h5 font-weight-bold">{{ formatPct(pctExcedenteSobreEsperado) }}</div>
        </v-card>
      </v-col>
      <v-col cols="12" md="3">
        <v-card class="pa-4 rounded-lg" outlined height="100">
          <div class="caption grey--text mb-1">Porcentaje de tiempo excedido de paros planeados vs. tiempo total</div>
          <div v-if="loading" class="d-flex align-center justify-center" style="height:44px">
            <v-progress-circular indeterminate color="primary" size="24" />
          </div>
          <div v-else class="text-h5 font-weight-bold">{{ formatPct(pctMalaEjecucion) }}</div>
        </v-card>
      </v-col>
    </v-row>

    <v-row class="mt-6" dense>
      <v-col cols="12" md="6">
        <v-card class="pa-5 rounded-lg" outlined height="120">
          <div class="caption grey--text mb-1">Tiempo adicional de paro en paros planeados (total)</div>
          <div v-if="loading" class="d-flex align-center justify-center" style="height:64px">
            <v-progress-circular indeterminate color="primary" size="28" />
          </div>
          <div v-else class="text-h4 font-weight-bold">{{ fmtHMS(totalExcedenteSeg) }}</div>
        </v-card>
      </v-col>

      <v-col cols="12" md="6">
        <v-card class="pa-5 rounded-lg" outlined height="120">
          <div class="caption grey--text mb-1">Tiempo promedio adicional de paro en paros planeados</div>
          <div v-if="loading" class="d-flex align-center justify-center" style="height:64px">
            <v-progress-circular indeterminate color="primary" size="28" />
          </div>
          <div v-else class="text-h4 font-weight-bold">{{ fmtHMS(promedioExcedenteSeg) }}</div>
        </v-card>
      </v-col>
    </v-row>

    <v-row dense class="mt-2">
      <v-col cols="12" md="6">
        <v-card outlined class="pa-4 rounded-lg" min-height="250">
          <div class="text-subtitle-2 font-weight-bold mb-1">Tiempo adicional de paro en paros planeados por causa (total)</div>
          <div v-if="loading" class="d-flex align-center justify-center" :style="`height:${hCausas}px`">
            <v-progress-circular indeterminate color="primary" size="32" />
          </div>
          <apexchart v-else type="bar" :height="hCausas" :options="barOptions" :series="seriesCausas" />
        </v-card>
      </v-col>

      <v-col cols="12" md="6">
        <v-card outlined class="pa-4 rounded-lg" min-height="250">
          <div class="text-subtitle-2 font-weight-bold mb-1">Tiempo promedio adicional de paro en paros planeados por causa</div>
          <div v-if="loading" class="d-flex align-center justify-center" :style="`height:${hAvgCausas}px`">
            <v-progress-circular indeterminate color="primary" size="32" />
          </div>
          <apexchart v-else type="bar" :height="hAvgCausas" :options="barOptions" :series="seriesAvgCausas" />
        </v-card>
      </v-col>
    </v-row>

    <v-row dense class="mt-2">
      <v-col cols="12" md="6">
        <v-card outlined class="pa-4 rounded-lg" min-height="250">
          <div class="text-subtitle-2 font-weight-bold mb-1">Tiempo adicional de paro en paros planeados por área (total)</div>
          <div v-if="loading" class="d-flex align-center justify-center" :style="`height:${hAreas}px`">
            <v-progress-circular indeterminate color="primary" size="32" />
          </div>
          <apexchart v-else type="bar" :height="hAreas" :options="barOptions" :series="seriesAreas" />
        </v-card>
      </v-col>

      <v-col cols="12" md="6">
        <v-card outlined class="pa-4 rounded-lg" min-height="250">
          <div class="text-subtitle-2 font-weight-bold mb-1">Tiempo promedio adicional de paro en paros planeados por área</div>
          <div v-if="loading" class="d-flex align-center justify-center" :style="`height:${hAvgAreas}px`">
            <v-progress-circular indeterminate color="primary" size="32" />
          </div>
          <apexchart v-else type="bar" :height="hAvgAreas" :options="barOptions" :series="seriesAvgAreas" />
        </v-card>
      </v-col>
    </v-row>

    <v-row dense class="mt-2">
      <v-col cols="12" md="6">
        <v-card outlined class="pa-4 rounded-lg" min-height="250">
          <div class="text-subtitle-2 font-weight-bold mb-1">Tiempo adicional de paro en paros planeados por máquina (total)</div>
          <div v-if="loading" class="d-flex align-center justify-center" :style="`height:${hMaquinas}px`">
            <v-progress-circular indeterminate color="primary" size="32" />
          </div>
          <apexchart v-else type="bar" :height="hMaquinas" :options="barOptions" :series="seriesMaquinas" />
        </v-card>
      </v-col>

      <v-col cols="12" md="6">
        <v-card outlined class="pa-4 rounded-lg" min-height="250">
          <div class="text-subtitle-2 font-weight-bold mb-1">Tiempo promedio adicional de paro en paros planeados por máquina</div>
          <div v-if="loading" class="d-flex align-center justify-center" :style="`height:${hAvgMaquinas}px`">
            <v-progress-circular indeterminate color="primary" size="32" />
          </div>
          <apexchart v-else type="bar" :height="hAvgMaquinas" :options="barOptions" :series="seriesAvgMaquinas" />
        </v-card>
      </v-col>
    </v-row>

    <v-row dense class="mt-2">
      <v-col cols="12" md="6">
        <v-card outlined class="pa-4 rounded-lg" min-height="250">
          <div class="text-subtitle-2 font-weight-bold mb-1">Tiempo adicional de paro en paros planeados por subcausa (total)</div>
          <div v-if="loading" class="d-flex align-center justify-center" :style="`height:${hSubcausas}px`">
            <v-progress-circular indeterminate color="primary" size="32" />
          </div>
          <apexchart v-else type="bar" :height="hSubcausas" :options="barOptions" :series="seriesSubcausas" />
        </v-card>
      </v-col>

      <v-col cols="12" md="6">
        <v-card outlined class="pa-4 rounded-lg" min-height="250">
          <div class="text-subtitle-2 font-weight-bold mb-1">Tiempo promedio adicional de paro en paros planeados por subcausa</div>
          <div v-if="loading" class="d-flex align-center justify-center" :style="`height:${hAvgSubcausas}px`">
            <v-progress-circular indeterminate color="primary" size="32" />
          </div>
          <apexchart v-else type="bar" :height="hAvgSubcausas" :options="barOptions" :series="seriesAvgSubcausas" />
        </v-card>
      </v-col>
    </v-row>

    <v-card outlined class="pa-4 rounded-lg mt-4">
      <div class="mb-2 d-flex align-center">
        <span class="mr-3 grey--text text--darken-1">Agrupar por:</span>
        <v-btn-toggle v-model="groupBy" group mandatory>
          <v-btn small value="area">Área</v-btn>
          <v-btn small value="maquina" class="mx-1">Máquina</v-btn>
          <v-btn small value="causa">Causa</v-btn>
          <v-btn small value="subcausa" class="ml-1">Subcausa</v-btn>
        </v-btn-toggle>
      </div>

      <div v-if="loading" class="d-flex align-center justify-center" style="height:300px">
        <v-progress-circular indeterminate color="primary" size="36" />
      </div>

      <v-data-table :headers="histHeaders" :items="histDecorado" :group-by="groupBy ? [groupBy] : []" item-key="rowid"
        dense class="elevation-1" :sort-by="['excedenteSeg']" :sort-desc="[true]" must-sort>
        <template v-slot:item.inicio="{ item }">
          {{ fmtFecha(item.inicio) }}
        </template>
        <template v-slot:item.esperadoSeg="{ item }">
          {{ fmtHMS(item.esperadoSeg) }}
        </template>
        <template v-slot:item.realSeg="{ item }">
          {{ fmtHMS(item.realSeg) }}
        </template>
        <template v-slot:item.excedenteSeg="{ item }">
          {{ fmtHMS(item.excedenteSeg) }}
        </template>
      </v-data-table>
    </v-card>
  </div>
</template>

<script>
import axios from 'axios'
import VueApexCharts from 'vue-apexcharts'

export default {
  components: { apexchart: VueApexCharts },
  data() {
    return {
      f: { desde: null, hasta: null, areasSel: [], maquinasSel: [] },
      areas: [], maquinas: [], loadingAreas: false, loadingMaquinas: false,
      loading: false, error: null,
      res: {
        excedentesPorMaquina: [],
        excedentesPorCausa: [],
        excedentesPorSubcausa: [],
        excedentesPorArea: [],
        historico: [],
        totalParos: 0,
        totalParosProgramados: 0,
        totalParosNoProgramados: 0,
        esperadoTotalProgramadosSeg: 0,
        realTotalProgramadosSeg: 0,
        excedenteTotalProgramadosSeg: 0,
        realTotalNoProgramadosSeg: 0,
        realTotalSeg: 0
      },
      groupBy: 'area'
    }
  },

  computed: {
    totalExcedenteSeg() {
      return (this.res.historico || []).reduce((a, r) => a + (r.excedenteSeg || 0), 0)
    },
    promedioExcedenteSeg() {
      const n = (this.res.historico || []).length
      return n ? Math.round(this.totalExcedenteSeg / n) : 0
    },
    programadosCnt() {
      return Number(this.res.totalParosProgramados || 0)
    },
    totalParosCnt() {
      return Number(this.res.totalParos || 0)
    },
    pctExcedenteSobreEsperado() {
      return this.safePct(this.totalExcedenteSeg, this.res.esperadoTotalProgramadosSeg)
    },
    pctMalaEjecucion() {
      return this.safePct(this.totalExcedenteSeg, this.res.realTotalSeg)
    },

    hCausas() { return this.calcBarHeight((this.res.excedentesPorCausa || []).length) },
    hMaquinas() { return this.calcBarHeight((this.res.excedentesPorMaquina || []).length) },
    hSubcausas() { return this.calcBarHeight((this.res.excedentesPorSubcausa || []).length) },
    hAreas() { return this.calcBarHeight((this.res.excedentesPorArea || []).length) },
    hAvgCausas() { return this.calcBarHeight(this._avgItemsBy('causa').length) },
    hAvgSubcausas() { return this.calcBarHeight(this._avgItemsBy('subcausa').length) },
    hAvgAreas() { return this.calcBarHeight(this._avgItemsBy('area').length) },
    hAvgMaquinas() { return this.calcBarHeight(this._avgItemsBy('maquina').length) },

    barOptions() {
      return {
        chart: { toolbar: { show: false }, fontFamily: 'Inter, Roboto, sans-serif' },
        plotOptions: { bar: { horizontal: true, barHeight: '45%', borderRadius: 8 } },
        dataLabels: {
          enabled: true,
          formatter: (val) => this.hoursToHMSLabel(val),
          style: { fontSize: '11px', fontWeight: 700, colors: ['#000'] },
          background: { enabled: true, foreColor: '#fff', padding: 4, borderRadius: 4, borderWidth: 0, opacity: 1 }
        },
        xaxis: { labels: { formatter: (v) => this.hoursToHMSLabel(v) } },
        grid: { borderColor: '#eef2ff' },
        colors: ['#2563eb'],
        tooltip: { y: { formatter: (v) => this.hoursToHMSLabel(v) } },
        states: { hover: { filter: { type: 'lighten', value: 0.05 } } }
      }
    },

    seriesCausas() { return [{ name: 'Excedente', data: this.decorateAndSort(this.res.excedentesPorCausa, 'causa') }] },
    seriesMaquinas() { return [{ name: 'Excedente', data: this.decorateAndSort(this.res.excedentesPorMaquina, 'maquina') }] },
    seriesSubcausas() { return [{ name: 'Excedente', data: this.decorateAndSort(this.res.excedentesPorSubcausa, 'subcausa') }] },
    seriesAreas() { return [{ name: 'Excedente', data: this.decorateAndSort(this.res.excedentesPorArea, 'area') }] },

    seriesAvgCausas() { return [{ name: 'Promedio', data: this.decorateFromXY(this._avgItemsBy('causa')) }] },
    seriesAvgSubcausas() { return [{ name: 'Promedio', data: this.decorateFromXY(this._avgItemsBy('subcausa')) }] },
    seriesAvgAreas() { return [{ name: 'Promedio', data: this.decorateFromXY(this._avgItemsBy('area')) }] },
    seriesAvgMaquinas() { return [{ name: 'Promedio', data: this.decorateFromXY(this._avgItemsBy('maquina')) }] },

    histHeaders() {
      return [
        { text: 'Inicio', value: 'inicio' },
        { text: 'Área', value: 'area' },
        { text: 'Máquina', value: 'maquina' },
        { text: 'Causa', value: 'causa' },
        { text: 'Subcausa', value: 'subcausa' },
        { text: 'Esperado', value: 'esperadoSeg' },
        { text: 'Real', value: 'realSeg' },
        { text: 'Excedente', value: 'excedenteSeg' }
      ]
    },
    histDecorado() {
      return (this.res.historico || []).map((r, i) => ({ ...r, rowid: i + 1 }))
    }
  },

  methods: {
    nombreMaquina(m) {
      if (!m) return ''
      const n = m.nombre || ''
      const marca = m.marca || ''
      const modelo = m.modelo || ''
      return `[${n}] - ${marca} ${modelo}`.trim()
    },
    initialize() { this.getAreas(); this.getMaquinas() },
    getAreas() {
      this.loadingAreas = true
      axios.get('/Areas')
        .then(res => { this.areas = Array.isArray(res.data) ? res.data : [] })
        .catch(() => { this.areas = [] })
        .finally(() => { this.loadingAreas = false })
    },
    getMaquinas() {
      this.loadingMaquinas = true
      axios.get('/MaquinaInstancias')
        .then(res => { this.maquinas = Array.isArray(res.data) ? res.data : [] })
        .catch(() => { this.maquinas = [] })
        .finally(() => { this.loadingMaquinas = false })
    },

    async buscar() {
      this.loading = true
      this.error = null
      this.res = {
        excedentesPorMaquina: [],
        excedentesPorCausa: [],
        excedentesPorSubcausa: [],
        excedentesPorArea: [],
        historico: [],
        totalParos: 0,
        totalParosProgramados: 0,
        totalParosNoProgramados: 0,
        esperadoTotalProgramadosSeg: 0,
        realTotalProgramadosSeg: 0,
        excedenteTotalProgramadosSeg: 0,
        realTotalNoProgramadosSeg: 0,
        realTotalSeg: 0
      }

      try {
        const desdeQS = encodeURIComponent(this.f.desde || '')
        const hastaQS = encodeURIComponent(this.f.hasta || '')
        const idsAreasQS = (this.f.areasSel || []).map(id => `IdsAreas=${encodeURIComponent(id)}`).join('&')
        const idsMaquinasQS = (this.f.maquinasSel || []).map(id => `IdsMaquinas=${encodeURIComponent(id)}`).join('&')
        let url = `/Reportes/ReporteParosProgramados?Desde=${desdeQS}&Hasta=${hastaQS}`
        if (idsAreasQS) url += `&${idsAreasQS}`
        if (idsMaquinasQS) url += `&${idsMaquinasQS}`

        const { data } = await axios.get(url)
        this.res = {
          excedentesPorMaquina: data.excedentesPorMaquina || [],
          excedentesPorCausa: data.excedentesPorCausa || [],
          excedentesPorSubcausa: data.excedentesPorSubcausa || [],
          excedentesPorArea: data.excedentesPorArea || [],
          historico: data.historico || [],
          totalParos: Number(data.totalParos || 0),
          totalParosProgramados: Number(data.totalParosProgramados || 0),
          totalParosNoProgramados: Number(data.totalParosNoProgramados || 0),
          esperadoTotalProgramadosSeg: Number(data.esperadoTotalProgramadosSeg || 0),
          realTotalProgramadosSeg: Number(data.realTotalProgramadosSeg || 0),
          excedenteTotalProgramadosSeg: Number(data.excedenteTotalProgramadosSeg || 0),
          realTotalNoProgramadosSeg: Number(data.realTotalNoProgramadosSeg || 0),
          realTotalSeg: Number(data.realTotalSeg || 0)
        }
      } catch (e) {
        console.error(e)
        this.error = 'No se pudo obtener la información.'
      } finally {
        this.loading = false
      }
    },

    calcBarHeight(n) {
      const rows = Number(n) || 0
      return Math.max(220, Math.min(900, 44 * rows + 60))
    },
    horas(seg) {
      return (Number(seg) || 0) / 3600
    },

    decorateAndSort(list, key) {
      const items = (list || []).map(r => ({
        x: (r && r[key]) || '—',
        y: this.horas(r?.excedenteSeg || 0)
      }))
      const sorted = items.slice().sort((a, b) => b.y - a.y)
      const reds = ['#ef4444', '#dc2626', '#b91c1c']
      const blue = '#2563eb'
      return sorted.map((it, idx) => ({ ...it, fillColor: idx < 3 ? reds[idx] : blue }))
    },

    _avgItemsBy(key) {
      const map = new Map()
      for (const r of (this.res.historico || [])) {
        const label = r[key] || '—'
        const exc = Number(r.excedenteSeg) || 0
        const cur = map.get(label) || { sum: 0, count: 0 }
        cur.sum += exc
        cur.count += 1
        map.set(label, cur)
      }
      return Array.from(map.entries()).map(([x, v]) => ({
        x,
        y: this.horas(v.count ? v.sum / v.count : 0)
      }))
    },

    decorateFromXY(items) {
      const arr = (items || []).slice().sort((a, b) => b.y - a.y)
      const reds = ['#ef4444', '#dc2626', '#b91c1c']
      const blue = '#2563eb'
      return arr.map((it, idx) => ({ ...it, fillColor: idx < 3 ? reds[idx] : blue }))
    },

    fmtHMS(seg) {
      const s = Math.max(0, Number(seg) || 0);
      const h = Math.floor(s / 3600);
      const m = Math.floor((s % 3600) / 60);
      const ss = Math.floor(s % 60);
      const mm = `${m}`.padStart(2, '0');
      const sss = `${ss}`.padStart(2, '0');
      return `${h} h ${mm} m ${sss} s`;
    },
    fmtFecha(iso) {
      if (!iso) return '—'
      const d = new Date(iso)
      const y = d.getFullYear()
      const mo = `${d.getMonth() + 1}`.padStart(2, '0')
      const da = `${d.getDate()}`.padStart(2, '0')
      const hh = `${d.getHours()}`.padStart(2, '0')
      const mi = `${d.getMinutes()}`.padStart(2, '0')
      const ss = `${d.getSeconds()}`.padStart(2, '0')
      return `${y}-${mo}-${da} ${hh}:${mi}:${ss}`
    },
    hoursToHMSLabel(h) {
      const totalSec = Math.round((Number(h) || 0) * 3600);
      return this.fmtHMS(totalSec);
    },
    formatPct(n) {
      return `${(Number(n) || 0).toFixed(2)}%`
    },
    safePct(num, den) {
      const n = Number(num) || 0
      const d = Number(den) || 0
      if (d <= 0) return 0
      return (n / d) * 100
    }
  },

  mounted() {
    this.initialize()
  }
}
</script>
